/**
 * HTTP layer for the site scheduling calendar (odl_app).
 *
 * Every call is scoped to one site's calendar
 * (`/odl-app/sites/<siteId>/calendar/…`). The backend expands recurrence and
 * resolves sun-relative times server-side, so this layer reads whole event
 * series, reads expanded occurrences for a date window, and writes through two
 * distinct surfaces: CRUD series writes (create / series-edit / series-delete,
 * `{data: row}` enveloped) and occurrence RPC routes (override a single
 * occurrence, delete a single occurrence, bare responses).
 *
 * Contains:
 * - fetchCalendarSeries / fetchCalendarOccurrences — reads
 * - createCalendarEvent / updateCalendarEventSeries / deleteCalendarEventSeries — series CRUD
 * - createCalendarOccurrenceOverride / deleteCalendarOccurrence — occurrence RPC
 */
import apiClient from '@sophocles/http-common';

const calendarBase = siteId => `/odl-app/sites/${siteId}/calendar`;

// =======================
// READS (bare)
// =======================

export const fetchCalendarSeries = siteId => apiClient.get(`${calendarBase(siteId)}/events/`).then(res => res.data);

export const fetchCalendarOccurrences = (siteId, { start, end, relay } = {}) => {
  const params = { start, end, ...(relay != null && { relay }) };
  return apiClient.get(`${calendarBase(siteId)}/occurrences/`, { params }).then(res => res.data);
};

// =======================
// SERIES CRUD ({data: row} envelope — unwrap b => b.data, no fallback)
// =======================

export const createCalendarEvent = (siteId, data) =>
  apiClient.post(`${calendarBase(siteId)}/events/`, data).then(res => res.data.data);

export const updateCalendarEventSeries = (siteId, eventId, data) =>
  apiClient.patch(`${calendarBase(siteId)}/events/${eventId}/`, data).then(res => res.data.data);

export const deleteCalendarEventSeries = (siteId, eventId) =>
  apiClient.delete(`${calendarBase(siteId)}/events/${eventId}/`).then(() => eventId);

// =======================
// OCCURRENCE RPC (bare responses: {parent, override} / {parent}|{deleted})
// =======================

export const createCalendarOccurrenceOverride = (siteId, eventId, body) =>
  apiClient.post(`${calendarBase(siteId)}/events/${eventId}/occurrence/`, body).then(res => res.data);

export const deleteCalendarOccurrence = (siteId, eventId, occurrenceDate) =>
  apiClient.delete(`${calendarBase(siteId)}/events/${eventId}/occurrence/${occurrenceDate}/`).then(res => res.data);
